import { client } from 'cccisd-apollo';
import { format } from 'date-fns';
import axios from 'cccisd-axios';
import _pick from 'lodash/pick';
import _uniq from 'lodash/uniq';
import _omit from 'lodash/omit';
import getPawnsQuery from './getPawns.graphql';
import getSessionQuery from './getSession.graphql';

export const formatDate = date => {
    return date ? format(new Date(date), 'MM/dd/yyyy') : null;
};

export const updateSessionDeploymentInfo = async (
    contacts = [{ pawnId: null, respondentHash: null, assigned: true }],
    session = { sessionId: null, deploymentId: null, sessionDeploymentInfo: {} },
    action = '' // "edit", used for editing the "assigned" flag. "delete" for removing contacts
) => {
    // Should take an array of contacts (pawnId, respondentHash)
    // Should find all open sessions for these contacts, and update the sessionDeploymentInfo if their pawnId not in that session
    // Should be called every time a new contact is added
    let response = null;

    // get the last saved session deployment info
    const resp = await client.query({
        query: getSessionQuery,
        variables: {
            planId: session.assignmentPlanId,
            sessionId: session.sessionId,
        },
        fetchPolicy: 'network-only',
    });

    let sessionDeploymentData = resp.data.flows.assignmentPlan.session.sessionDeploymentInfo || {};

    for (const contact of contacts) {
        if (action === 'delete') {
            sessionDeploymentData = _omit(sessionDeploymentData, contact.pawnId);
        } else if (action === 'edit') {
            if (sessionDeploymentData[contact.pawnId]) {
                sessionDeploymentData[contact.pawnId].assigned = contact.assigned;
            } else {
                const res = await axios.get(
                    window.cccisd.boilerplate.route('api.assessment.getSessionDeployment', {
                        deploymentId: session.deploymentId,
                        pawnId: contact.pawnId,
                        hash: contact.respondentHash,
                    }),
                    { params: { forceRepeat: true } }
                );
                sessionDeploymentData[contact.pawnId] = {
                    pawnId: contact.pawnId,
                    deploymentId: res.data.data.deploymentId,
                    assigned: contact.assigned,
                };
            }
        } else {
            // if sessionDeploymentInfo already has the pawnId, skip
            if (sessionDeploymentData[contact.pawnId]) {
                continue;
            }

            const res = await axios.get(
                window.cccisd.boilerplate.route('api.assessment.getSessionDeployment', {
                    deploymentId: session.deploymentId,
                    pawnId: contact.pawnId,
                    hash: contact.respondentHash,
                }),
                { params: { forceRepeat: true } }
            );

            sessionDeploymentData = {
                ...sessionDeploymentData,
                [contact.pawnId]: {
                    pawnId: contact.pawnId,
                    deploymentId: res.data.data.deploymentId,
                    assigned: true,
                },
            };
        }
    }
    if (contacts.length > 0) {
        response = await axios.put(
            window.cccisd.boilerplate.route('api.assignment.session.update', {
                session: session.sessionId,
            }),
            {
                sessionDeploymentInfo: sessionDeploymentData,
            }
        );
    }
    return response;
};

export const getPawnsInfo = async respondentIds => {
    let pawnsInfo = [];
    try {
        const resp = await client.query({
            query: getPawnsQuery,
            variables: {
                pawnIds: respondentIds,
            },
            fetchPolicy: 'network-only',
        });

        pawnsInfo = resp.data.roles.anyRoleList;
    } catch (e) {
        throw e;
    }

    return pawnsInfo;
};

const getFlowLanguages = metadata => {
    let languages = new Set();
    if (metadata.surveyInfo && metadata.surveyInfo.flowList) {
        metadata.surveyInfo.flowList.forEach(flow => {
            languages.add(flow.language);
        });
    }
    return [...languages];
};

export const formatResourcesForCollection = resourceList => {
    const formattedResources = resourceList.map(r => {
        const tags = r.tagList.reduce((acc, cur) => ({ ...acc, [cur.tagId]: cur.handle }), {});
        return {
            id: r.resourceId,
            label: r.label,
            description: r.description,
            tags,
            languages: getFlowLanguages(JSON.parse(r.metadata)),
            resource: { published_metadata: r.metadata },
        };
    });

    return formattedResources;
};

export const filterSessionSettings = settings => {
    const filteredSettings = {
        ...settings,
        sessionCollection: settings.sessionCollection.map(sc => {
            return {
                ...sc,
                resource: _pick(sc.resource, [
                    'id',
                    'handle',
                    'published_label',
                    'published_description',
                    'published_metadata',
                ]),
            };
        }),
    };
    return filteredSettings;
};

export const getStudentContactInfo = (plan, row, contactTypes) => {
    if (plan && row) {
        const { sessionList, assignmentPlanId } = plan;
        // all reporter types from sessionlist
        const sessionReporters = [];
        const typesOfContacts = [];
        const otherPlanContacts = [{ value: '', label: '-- Choose a contact --' }];
        const notificationList = [];
        const contacts = row['childRoles.contactList'];

        sessionList.forEach(sess => {
            const { settings } = sess;
            if (settings.reporter && settings.reporter !== 'Student') {
                sessionReporters.push(settings.reporter);
            }
        });

        const uniqueReporters = _uniq(sessionReporters);

        contacts.forEach(cont => {
            const planIds = cont.fields.assignment_plan_ids ? JSON.parse(cont.fields.assignment_plan_ids) : [];
            const contactType = cont.fields.contactType;

            if (contactType) {
                if (planIds.includes(assignmentPlanId)) {
                    typesOfContacts.push(contactType);
                } else if (uniqueReporters.includes(contactType)) {
                    otherPlanContacts.push({
                        value: cont.pawn.pawnId,
                        label: `${cont.fields.contactName} (${cont.fields.contactEmail}) (${cont.fields.contactType})`,
                        type: cont.fields.contactType,
                    });
                }
            }
        });

        const uniqueContacts = _uniq(typesOfContacts);

        uniqueReporters.forEach(rep => {
            const match = uniqueContacts.find(cont => cont === rep);
            if (!match) {
                notificationList.push(rep);
            }
        });

        let filteredOptions = uniqueReporters.map(cont => {
            return { value: cont, label: cont };
        });

        if (contactTypes) {
            filteredOptions = contactTypes.map(contact => {
                return { value: contact, label: contact };
            });
        }

        if (filteredOptions.length > 1) {
            filteredOptions.unshift({ value: '', label: '-- Choose a type --' });
        }

        return {
            notificationCount: notificationList.length > 0 ? notificationList.length : null,
            notificationList,
            options: filteredOptions,
            otherPlanContacts,
        };
    }
};
